import type { TypographyStyle } from '../components/TypographyElement/types';
import { useQuery } from '@tanstack/react-query';
import FetchPromise from '~/agent/fetch-promise';
import { useEnterpriseId } from '~/features/common/providers/useEnterpriseId';
import { TYPOGRAPHY_STYLE_QUERY_KEY } from '../utils/typographyKeyFactory';

// Record<string, never> is used to represent an empty object
const getTypographyStyle = (enterpriseId?: number): Promise<TypographyStyle | Record<string, never>> =>
  FetchPromise.get(`typography_styles/?enterprise_id=${enterpriseId}`);

export const useGetTypographyStyle = ({
  onSuccess,
  onError,
  refetchOnMount = false,
  staleTime = 30 * 60 * 1000, // 30 minutes, so we probably remember the style as they navigate between tasks
  cacheTime = 35 * 60 * 1000, // a bit longer than staleTime, so if they navigate away & back within a few min, we still have the style
}: {
  onSuccess?: (data: TypographyStyle) => void;
  onError?: () => void;
  refetchOnMount?: boolean;
  staleTime?: number;
  cacheTime?: number;
} = {}) => {
  const enterpriseId = useEnterpriseId();

  return useQuery({
    queryKey: [TYPOGRAPHY_STYLE_QUERY_KEY],
    queryFn: () => (enterpriseId ? getTypographyStyle(enterpriseId) : getTypographyStyle()),
    onSuccess,
    onError,
    refetchOnMount,
    staleTime,
    cacheTime,
  });
};
